import { useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import { Camera, AlertTriangle, CheckCircle2, Clock, Car, MapPin, PenLine } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog"
import { supabase } from "@/integrations/supabase/client"
import { createSignedFileUrl, PHOTO_BUCKET } from "@/lib/vehicleStorage"
import { criteriosData } from "@/lib/criteriosData"
import { SignaturePad } from "@/components/SignaturePad"

// Oficina-leve criteria (mirrors Avaliacao360.tsx)
const CRITERIOS_OFICINA_LEVE = [
  { id: "oficina_frontal_1", nome: "Frente do veículo", categoria: "Frontal" },
  { id: "oficina_traseira_1", nome: "Traseira do veículo", categoria: "Traseira" },
  { id: "oficina_latdir_1", nome: "Lateral direita", categoria: "Lateral Direita" },
  { id: "oficina_latesq_1", nome: "Lateral esquerda", categoria: "Lateral Esquerda" },
  { id: "oficina_painel_1", nome: "Quilometragem e painel", categoria: "Painel e Instrumentos" },
  { id: "oficina_interior_1", nome: "Interior e pertences", categoria: "Interior" },
]

const LABEL_CATEGORIA: Record<string, string> = {
  "Frontal": "Frente",
  "Traseira": "Traseira",
  "Lateral Direita": "Lat. Direita",
  "Lateral Esquerda": "Lat. Esquerda",
  "Painel e Instrumentos": "Painel / km",
  "Interior": "Interior",
  "Motor": "Motor",
  "Detalhes Extras": "Extras",
}

function resolverNomeCriterio(criterio_id: string): { nome: string; categoria: string } {
  const oficina = CRITERIOS_OFICINA_LEVE.find(c => c.id === criterio_id)
  if (oficina) return { nome: oficina.nome, categoria: oficina.categoria }
  const loja = criteriosData.find(c => c.id === criterio_id)
  if (loja) return { nome: loja.nome, categoria: loja.categoria }
  const prefixo = criterio_id.split("_")[0]
  return { nome: criterio_id, categoria: prefixo }
}

interface TermoData {
  criado_em: string
  observacoes: string | null
  loja_nome: string
  cliente_nome: string | null
  cliente_telefone: string | null
  cliente_assinatura: string | null
  cliente_assinado_em: string | null
  veiculo: {
    placa: string
    marca: string
    modelo: string
    ano: number
    anoModelo: string | null
    cor: string | null
    combustivel: string | null
    quilometragem: number | null
  }
  criterios: Array<{
    criterio_id: string
    status: string
    observacoes: string | null
  }>
  fotos: Array<{
    categoria: string
    url: string
    descricao: string | null
  }>
}

interface FotoResolvida {
  categoria: string
  url: string
  descricao: string | null
}

export default function TermoPublico() {
  const { token } = useParams<{ token: string }>()
  const [dados, setDados] = useState<TermoData | null>(null)
  const [fotosResolvidas, setFotosResolvidas] = useState<FotoResolvida[]>([])
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [clienteNome, setClienteNome] = useState("")
  const [assinaturaUrl, setAssinaturaUrl] = useState("")
  const [assinando, setAssinando] = useState(false)
  const [assinado, setAssinado] = useState(false)

  useEffect(() => {
    if (token) void fetchTermo(token)
  }, [token])

  const fetchTermo = async (t: string) => {
    try {
      const { data, error } = await supabase.rpc("get_termo_publico", { p_token: t })

      if (error || !data) {
        setNotFound(true)
        return
      }

      const termo = data as TermoData
      setDados(termo)
      setClienteNome(termo.cliente_nome ?? "")
      if (termo.cliente_assinado_em) setAssinado(true)

      const resolvidas = await Promise.all(
        termo.fotos.map(async (foto) => ({
          ...foto,
          url: (await createSignedFileUrl(PHOTO_BUCKET, foto.url)) ?? foto.url,
        }))
      )
      setFotosResolvidas(resolvidas)
    } catch {
      setNotFound(true)
    } finally {
      setLoading(false)
    }
  }

  const handleAssinar = async () => {
    if (!token || !clienteNome.trim() || !assinaturaUrl) return
    setAssinando(true)
    try {
      const { data, error } = await supabase.rpc("assinar_termo_publico", {
        p_token: token,
        p_nome: clienteNome.trim(),
        p_assinatura: assinaturaUrl,
      })
      if (error || !data?.ok) throw new Error(data?.motivo ?? "erro")
      setAssinado(true)
    } catch {
      // silently stays on form — user can retry
    } finally {
      setAssinando(false)
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  if (notFound || !dados) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center">
        <AlertTriangle className="h-12 w-12 text-muted-foreground opacity-40" />
        <h1 className="text-lg font-semibold">Link inválido ou expirado</h1>
        <p className="text-sm text-muted-foreground max-w-xs">
          Este link não corresponde a nenhum termo de entrada registrado.
        </p>
      </div>
    )
  }

  const { veiculo, criterios, observacoes, loja_nome, criado_em } = dados

  const fotosGerais = fotosResolvidas.filter(f =>
    f.descricao?.toLowerCase().includes("principal") && !f.categoria.startsWith("criterio_")
  )

  const danos = criterios.filter(c => c.status === "reprovado")
  const atencao = criterios.filter(c => c.status === "atencao")
  const aprovados = criterios.filter(c => c.status === "aprovado")

  const dataEntrada = new Date(criado_em).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })

  const getFotoDano = (criterio_id: string) =>
    fotosResolvidas.find(f => f.categoria === `criterio_reprovado_${criterio_id}`)

  const getFotoAtencao = (criterio_id: string) =>
    fotosResolvidas.find(f => f.categoria === `criterio_atencao_${criterio_id}`)

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header da oficina */}
      <div className="bg-white border-b px-4 py-4 text-center shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">
          Termo de Entrada
        </p>
        <h1 className="text-lg font-bold text-foreground">{loja_nome}</h1>
        <div className="mt-2 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
          <Clock className="h-3.5 w-3.5" />
          <span>{dataEntrada}</span>
        </div>
      </div>

      <div className="mx-auto max-w-lg px-4 py-5 space-y-5">

        {/* Identificação do veículo */}
        <div className="rounded-2xl bg-white border shadow-sm p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
              <Car className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-lg font-bold tracking-widest text-foreground">
                  {veiculo.placa}
                </span>
                {veiculo.cor && (
                  <Badge variant="secondary" className="text-xs">{veiculo.cor}</Badge>
                )}
              </div>
              <p className="mt-0.5 text-sm font-medium text-foreground">
                {veiculo.marca} {veiculo.modelo}
              </p>
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                <span>{veiculo.ano}{veiculo.anoModelo && veiculo.anoModelo !== String(veiculo.ano) ? `/${veiculo.anoModelo}` : ""}</span>
                {veiculo.quilometragem != null && (
                  <span>{veiculo.quilometragem.toLocaleString("pt-BR")} km</span>
                )}
                {veiculo.combustivel && <span>{veiculo.combustivel}</span>}
              </div>
            </div>
          </div>
        </div>

        {(dados.cliente_nome || dados.cliente_telefone) && (
          <div className="rounded-2xl bg-white border shadow-sm p-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Cliente
            </p>
            {dados.cliente_nome && (
              <p className="text-sm font-medium text-foreground">{dados.cliente_nome}</p>
            )}
            {dados.cliente_telefone && (
              <p className="mt-0.5 text-sm text-muted-foreground">{dados.cliente_telefone}</p>
            )}
          </div>
        )}

        {/* Resumo rápido de status */}
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-xl bg-white border shadow-sm p-3 text-center">
            <p className="text-2xl font-bold text-foreground">{aprovados.length}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">OK</p>
          </div>
          <div className="rounded-xl bg-amber-50 border border-amber-200 shadow-sm p-3 text-center">
            <p className="text-2xl font-bold text-amber-700">{atencao.length}</p>
            <p className="text-[11px] text-amber-600 mt-0.5">Observação</p>
          </div>
          <div className="rounded-xl bg-red-50 border border-red-200 shadow-sm p-3 text-center">
            <p className="text-2xl font-bold text-red-700">{danos.length}</p>
            <p className="text-[11px] text-red-600 mt-0.5">Dano</p>
          </div>
        </div>

        {/* Fotos gerais */}
        {fotosGerais.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Camera className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-sm font-semibold text-foreground">Registro fotográfico</h2>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {fotosGerais.map((foto, i) => (
                <Dialog key={i}>
                  <DialogTrigger asChild>
                    <div className="group relative aspect-square cursor-pointer overflow-hidden rounded-xl border bg-muted shadow-sm">
                      <img
                        src={foto.url}
                        alt={LABEL_CATEGORIA[foto.categoria] ?? foto.categoria}
                        className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/65 to-transparent px-2.5 py-2">
                        <p className="text-[11px] font-medium text-white leading-tight">
                          {LABEL_CATEGORIA[foto.categoria] ?? foto.categoria}
                        </p>
                      </div>
                    </div>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg flex flex-col items-center gap-2 p-4">
                    <p className="text-sm font-medium">{LABEL_CATEGORIA[foto.categoria] ?? foto.categoria}</p>
                    <img
                      src={foto.url}
                      alt={LABEL_CATEGORIA[foto.categoria] ?? foto.categoria}
                      className="rounded-lg object-contain w-full"
                      style={{ maxHeight: "75vh" }}
                    />
                  </DialogContent>
                </Dialog>
              ))}
            </div>
          </section>
        )}

        {/* Danos registrados */}
        {danos.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <h2 className="text-sm font-semibold text-foreground">
                Danos registrados
              </h2>
              <Badge variant="outline" className="ml-auto text-xs bg-red-50 text-red-700 border-red-200">
                {danos.length} {danos.length === 1 ? "item" : "itens"}
              </Badge>
            </div>
            <div className="space-y-2">
              {danos.map((c, i) => {
                const { nome, categoria } = resolverNomeCriterio(c.criterio_id)
                const foto = getFotoDano(c.criterio_id)
                return (
                  <div key={i} className="flex gap-3 rounded-xl border bg-white shadow-sm p-3 items-start">
                    {foto ? (
                      <Dialog>
                        <DialogTrigger asChild>
                          <img
                            src={foto.url}
                            alt={nome}
                            className="h-16 w-16 shrink-0 cursor-pointer rounded-lg object-cover border"
                          />
                        </DialogTrigger>
                        <DialogContent className="max-w-lg flex flex-col items-center gap-2 p-4">
                          <p className="text-sm font-medium">{nome}</p>
                          <img
                            src={foto.url}
                            alt={nome}
                            className="rounded-lg object-contain w-full"
                            style={{ maxHeight: "75vh" }}
                          />
                        </DialogContent>
                      </Dialog>
                    ) : (
                      <div className="h-16 w-16 shrink-0 rounded-lg border bg-red-50 flex items-center justify-center">
                        <AlertTriangle className="h-5 w-5 text-red-300" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground leading-snug">{nome}</p>
                      <p className="text-xs text-muted-foreground">{categoria}</p>
                      {c.observacoes && (
                        <p className="mt-1 text-xs text-muted-foreground italic">{c.observacoes}</p>
                      )}
                    </div>
                    <Badge variant="destructive" className="text-[10px] shrink-0">Dano</Badge>
                  </div>
                )
              })}
            </div>
          </section>
        )}

        {/* Observações importantes */}
        {atencao.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <h2 className="text-sm font-semibold text-foreground">Observações importantes</h2>
              <Badge variant="outline" className="ml-auto text-xs bg-amber-50 text-amber-700 border-amber-200">
                {atencao.length} {atencao.length === 1 ? "item" : "itens"}
              </Badge>
            </div>
            <div className="space-y-2">
              {atencao.map((c, i) => {
                const { nome, categoria } = resolverNomeCriterio(c.criterio_id)
                const foto = getFotoAtencao(c.criterio_id)
                return (
                  <div key={i} className="flex gap-3 rounded-xl border bg-white shadow-sm p-3 items-start">
                    {foto ? (
                      <Dialog>
                        <DialogTrigger asChild>
                          <img
                            src={foto.url}
                            alt={nome}
                            className="h-16 w-16 shrink-0 cursor-pointer rounded-lg object-cover border"
                          />
                        </DialogTrigger>
                        <DialogContent className="max-w-lg flex flex-col items-center gap-2 p-4">
                          <p className="text-sm font-medium">{nome}</p>
                          <img
                            src={foto.url}
                            alt={nome}
                            className="rounded-lg object-contain w-full"
                            style={{ maxHeight: "75vh" }}
                          />
                        </DialogContent>
                      </Dialog>
                    ) : (
                      <div className="h-16 w-16 shrink-0 rounded-lg border bg-amber-50 flex items-center justify-center">
                        <AlertTriangle className="h-5 w-5 text-amber-300" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground leading-snug">{nome}</p>
                      <p className="text-xs text-muted-foreground">{categoria}</p>
                      {c.observacoes && (
                        <p className="mt-1 text-xs text-muted-foreground italic">{c.observacoes}</p>
                      )}
                    </div>
                    <Badge variant="outline" className="text-[10px] shrink-0 border-amber-300 bg-amber-50 text-amber-700">
                      Atenção
                    </Badge>
                  </div>
                )
              })}
            </div>
          </section>
        )}

        {/* Tudo OK */}
        {danos.length === 0 && atencao.length === 0 && criterios.length > 0 && (
          <div className="flex flex-col items-center gap-2 rounded-2xl border bg-green-50 border-green-200 p-6 text-center">
            <CheckCircle2 className="h-8 w-8 text-green-500" />
            <p className="text-sm font-medium text-green-800">Nenhum dano registrado</p>
            <p className="text-xs text-green-700">
              O veículo foi registrado sem danos ou observações no momento da entrada.
            </p>
          </div>
        )}

        {/* Observações gerais */}
        {observacoes && (
          <section className="rounded-2xl border bg-white shadow-sm p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Observações
            </p>
            <p className="text-sm text-foreground whitespace-pre-line">{observacoes}</p>
          </section>
        )}

        {/* Assinatura do cliente */}
        <section className="rounded-2xl border bg-white shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 px-4 pt-4 pb-3 border-b">
            <PenLine className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold text-foreground">Ciência do cliente</h2>
          </div>

          {assinado ? (
            <div className="p-4 space-y-3">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                <span className="text-sm font-medium">Ciência registrada</span>
              </div>
              {(dados?.cliente_assinatura || assinaturaUrl) && (
                <img
                  src={dados?.cliente_assinatura ?? assinaturaUrl}
                  alt="Assinatura"
                  className="w-full max-w-xs rounded-lg border bg-white object-contain"
                  style={{ height: 100 }}
                />
              )}
              {dados?.cliente_nome && (
                <p className="text-sm font-medium text-foreground">{dados.cliente_nome}</p>
              )}
              {dados?.cliente_telefone && (
                <p className="text-xs text-muted-foreground">{dados.cliente_telefone}</p>
              )}
              {dados?.cliente_assinado_em && (
                <p className="text-xs text-muted-foreground">
                  {new Date(dados.cliente_assinado_em).toLocaleString("pt-BR", {
                    day: "2-digit", month: "2-digit", year: "numeric",
                    hour: "2-digit", minute: "2-digit",
                  })}
                </p>
              )}
            </div>
          ) : (
            <div className="p-4 space-y-4">
              <p className="text-xs text-muted-foreground">
                Assine abaixo para confirmar que você está ciente do estado do veículo no momento da entrada.
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="cliente-nome" className="text-xs">Seu nome completo</Label>
                <Input
                  id="cliente-nome"
                  value={clienteNome}
                  onChange={e => setClienteNome(e.target.value)}
                  placeholder="Nome de quem assina"
                  className="h-9"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Assinatura</Label>
                <SignaturePad value={assinaturaUrl} onChange={setAssinaturaUrl} />
              </div>
              <Button
                className="w-full"
                onClick={handleAssinar}
                disabled={assinando || !clienteNome.trim() || !assinaturaUrl}
              >
                {assinando
                  ? <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-current" />
                  : <PenLine className="mr-2 h-4 w-4" />
                }
                Confirmar ciência
              </Button>
            </div>
          )}
        </section>

        {/* Rodapé */}
        <div className="rounded-2xl border bg-white shadow-sm p-4 text-center space-y-1">
          <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
            <MapPin className="h-3 w-3" />
            <span>Documento gerado por <strong>{loja_nome}</strong></span>
          </div>
          <p className="text-xs text-muted-foreground">{dataEntrada}</p>
          <p className="text-[11px] text-muted-foreground/60 mt-2">
            Este documento comprova o estado do veículo no momento da entrada na oficina.
          </p>
        </div>

      </div>
    </div>
  )
}
